import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import Marquee from "../components/Marquee";
import EnquiryForm from "../components/EnquiryForm";
import { MaskedLine, Reveal, Stagger, StaggerItem, EASE } from "../components/Reveal";
import { IMG, SERVICES, PROCESS, PROJECTS, STATS, TESTIMONIALS, CONTACT, whatsappLink } from "../data/content";

function Hero({ delay }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[620px] overflow-hidden bg-espresso">
      <motion.div className="absolute inset-0" style={reduce ? {} : { y: bgY }}>
        <img
          src={IMG.hero}
          alt="Warm modern living room designed by Aurea Living"
          className="h-full w-full scale-[1.12] object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-espresso/90 via-espresso/40 to-espresso/55" />

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full container-lux flex flex-col justify-end pb-16 sm:pb-20">
        <motion.p
          className="overline-label !text-gold"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay }}
        >
          Premium Interior Design Studio · Hyderabad
        </motion.p>

        <h1 className="mt-6 font-serif font-light text-[13vw] sm:text-6xl lg:text-[5.4rem] leading-[1.02] tracking-tight text-ivory">
          <MaskedLine delay={delay}>
            Interiors composed
          </MaskedLine>
          <MaskedLine delay={delay + 0.14}>
            in light <span className="italic text-gold">&amp; gold.</span>
          </MaskedLine>
        </h1>

        <motion.p
          className="mt-6 max-w-xl text-[15px] sm:text-lg font-light leading-relaxed text-ivory/75"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: delay + 0.45 }}
        >
          Aurea Living designs and builds warm, editorial homes and workspaces
          across Hyderabad — from first sketch to final styling, under one roof.
        </motion.p>

        <motion.div
          className="mt-9 flex flex-wrap gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: delay + 0.6 }}
        >
          <Link to="/contact" data-testid="hero-cta-consultation" className="btn-bronze">
            Book a Design Consultation
          </Link>
          <Link
            to="/portfolio"
            data-testid="hero-cta-portfolio"
            className="inline-flex items-center gap-2 border border-ivory/35 px-7 py-3.5 text-[13px] font-medium tracking-[0.12em] uppercase text-ivory transition-all duration-500 hover:border-gold hover:text-gold hover:-translate-y-0.5"
          >
            View Our Work
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-8 right-6 sm:right-12 z-10 hidden sm:flex flex-col items-center gap-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: delay + 1, duration: 1 }}
        style={{ opacity: fade }}
      >
        <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-ivory/60 [writing-mode:vertical-rl]">
          Scroll
        </span>
        <span className="h-14 w-px bg-ivory/30 overflow-hidden">
          <motion.span
            className="block h-1/2 w-full bg-gold"
            animate={{ y: ["-100%", "220%"] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.div>
    </section>
  );
}

function Narrative() {
  return (
    <section className="container-lux py-24 lg:py-32" data-testid="narrative-section">
      <div className="grid gap-14 lg:grid-cols-12 items-center">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="overline-label">The Studio</p>
            <h2 className="mt-5 font-serif font-light text-3xl sm:text-4xl lg:text-[2.9rem] leading-[1.12] text-ink text-balance">
              A house should hold you the way light holds a wall — softly, and
              without asking.
            </h2>
            <p className="mt-7 max-w-xl text-[15px] sm:text-base font-light leading-relaxed text-stone-warm">
              Founded in Hyderabad, Aurea Living is a design-and-build atelier
              where European precision meets Indian craft. Architects, interior
              designers and our own master carpenters work as one team — so the
              drawing reaches your site unwatered, and the site reaches the
              promised date.
            </p>
          </Reveal>
          <Stagger className="mt-12 grid grid-cols-3 gap-6 border-t border-bronze/15 pt-8">
            {STATS.map((s) => (
              <StaggerItem key={s.label}>
                <p className="font-serif text-3xl sm:text-4xl text-bronze" data-testid="stat-value">
                  {s.value}
                </p>
                <p className="mt-2 text-[11px] sm:text-xs font-medium tracking-wide text-stone-warm leading-snug">
                  {s.label}
                </p>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.1}>
            <Link
              to="/about"
              data-testid="narrative-about-link"
              className="link-line inline-block mt-10 text-[13px] tracking-[0.16em] uppercase text-ink"
            >
              Our Story →
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="img-arch overflow-hidden aspect-[3/4] max-w-sm mx-auto lg:ml-auto">
            <img
              src={IMG.narrative}
              alt="Aurea Living interior in warm neutral palette"
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-[1.4s] ease-luxe hover:scale-105"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FeaturedWork() {
  const featured = PROJECTS.slice(0, 5);
  const spans = [
    "md:col-span-7 h-[320px] md:h-[560px]",
    "md:col-span-5 h-[320px] md:h-[560px]",
    "md:col-span-4 h-[300px]",
    "md:col-span-4 h-[300px]",
    "md:col-span-4 h-[300px]",
  ];
  return (
    <section className="container-lux pb-24 lg:pb-32" data-testid="featured-work-section">
      <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <p className="overline-label">Selected Work</p>
          <h2 className="mt-4 font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-ink">
            Recent <span className="italic text-bronze">chapters</span>
          </h2>
        </div>
        <Link to="/portfolio" data-testid="featured-work-viewall" className="link-line text-[13px] tracking-[0.16em] uppercase text-ink">
          View all projects →
        </Link>
      </Reveal>

      <Stagger className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-5" gap={0.1}>
        {featured.map((p, i) => (
          <StaggerItem key={p.id} className={`group relative overflow-hidden ${spans[i]}`}>
            <Link to="/portfolio" data-testid={`featured-project-${p.id}`} className="block h-full">
              <img
                src={p.cover}
                alt={p.title}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[1.2s] ease-luxe group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-espresso/80 via-espresso/10 to-transparent opacity-90 transition-opacity duration-500 group-hover:opacity-100" />
              <div className="absolute bottom-0 inset-x-0 p-6 flex items-end justify-between gap-4">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold">
                    {p.location} · {p.category}
                  </p>
                  <p className="mt-1.5 font-serif text-2xl sm:text-[26px] text-ivory">
                    {p.title}
                  </p>
                </div>
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-ivory/40 text-ivory opacity-0 translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
                  →
                </span>
              </div>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function ServicesIndex() {
  return (
    <section className="bg-sand py-24 lg:py-32" data-testid="services-index-section">
      <div className="container-lux">
        <Reveal className="mb-14 max-w-2xl">
          <p className="overline-label">What We Do</p>
          <h2 className="mt-4 font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-ink">
            Six crafts, <span className="italic text-bronze">one contract.</span>
          </h2>
        </Reveal>
        <Stagger>
          {SERVICES.map((s) => (
            <StaggerItem key={s.num}>
              <Link
                to="/services"
                data-testid={`services-index-row-${s.num}`}
                className="group grid grid-cols-12 items-center gap-4 border-t border-bronze/20 py-7 sm:py-9 transition-colors duration-500 hover:bg-white/60"
              >
                <span className="col-span-2 sm:col-span-1 font-mono text-sm text-bronze">
                  {s.num}
                </span>
                <span className="col-span-10 sm:col-span-4 font-serif text-2xl sm:text-3xl text-ink transition-transform duration-500 ease-luxe group-hover:translate-x-2">
                  {s.title}
                </span>
                <span className="hidden sm:block col-span-6 text-sm font-light text-stone-warm leading-relaxed">
                  {s.desc}
                </span>
                <span className="hidden sm:grid col-span-1 justify-items-end text-bronze text-xl transition-transform duration-500 ease-luxe group-hover:translate-x-1.5">
                  →
                </span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal className="mt-12 border-t border-bronze/20 pt-8">
          <Link to="/services" data-testid="services-index-more" className="link-line text-[13px] tracking-[0.16em] uppercase text-ink">
            Explore all services →
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function ProcessTeaser() {
  return (
    <section className="bg-espresso text-ivory py-24 lg:py-32" data-testid="process-teaser-section">
      <div className="container-lux">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-16">
          <div>
            <p className="overline-label !text-gold">How We Work</p>
            <h2 className="mt-4 font-serif font-light text-3xl sm:text-4xl lg:text-5xl">
              The Aurea Process
            </h2>
          </div>
          <Link to="/process" data-testid="process-teaser-link" className="link-line text-[13px] tracking-[0.16em] uppercase text-ivory/80">
            In detail →
          </Link>
        </Reveal>
        <Stagger className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5" gap={0.1}>
          {PROCESS.map((p) => (
            <StaggerItem key={p.num} className="border-t border-ivory/15 pt-6">
              <p className="font-serif text-5xl text-gold/30">{p.num}</p>
              <p className="mt-4 font-serif text-2xl">{p.title}</p>
              <p className="mt-3 text-[13px] font-light leading-relaxed text-ivory/55">
                {p.desc.split(".")[0]}.
              </p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="container-lux py-24 lg:py-32" data-testid="testimonials-section">
      <Reveal className="mb-14">
        <p className="overline-label">Kind Words</p>
        <h2 className="mt-4 font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-ink">
          From the homes <span className="italic text-bronze">we've lived in.</span>
        </h2>
      </Reveal>
      <Stagger className="grid gap-10 md:grid-cols-3">
        {TESTIMONIALS.map((t) => (
          <StaggerItem key={t.name} className="border-t border-bronze pt-6">
            <p className="font-serif text-xl sm:text-[22px] font-light leading-relaxed text-ink">
              “{t.quote}”
            </p>
            <p className="mt-6 text-[13px] font-medium text-ink">{t.name}</p>
            <p className="mt-1 text-[11px] font-mono uppercase tracking-[0.18em] text-stone-500">
              {t.project}
            </p>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="bg-sand py-24 lg:py-32" data-testid="home-cta-section">
      <div className="container-lux grid gap-14 lg:grid-cols-2 items-center">
        <Reveal>
          <p className="overline-label">Begin</p>
          <h2 className="mt-5 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink">
            Let's compose <br />
            <span className="italic text-bronze">your space.</span>
          </h2>
          <p className="mt-6 max-w-md text-[15px] font-light leading-relaxed text-stone-warm">
            Tell us about your site and how you live in it. Our design team
            replies within one working day — with a point of view, not a
            brochure.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-cta-whatsapp"
              className="btn-outline"
            >
              WhatsApp us first
            </a>
            <a href={`tel:+${CONTACT.whatsapp}`} data-testid="home-cta-call" className="btn-bronze">
              Call {CONTACT.phoneDisplay}
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="bg-white border border-bronze/10 p-7 sm:p-10 shadow-[0_30px_60px_-30px_rgba(28,25,23,0.25)]">
            <p className="font-serif text-2xl text-ink mb-7">Start your enquiry</p>
            <EnquiryForm compact />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home({ heroDelay = 0.35 }) {
  return (
    <>
      <Hero delay={heroDelay} />
      <Marquee />
      <Narrative />
      <FeaturedWork />
      <ServicesIndex />
      <ProcessTeaser />
      <Testimonials />
      <CtaBand />
    </>
  );
}
